import React, { useCallback, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import PartnerRow from '@/components/PartnerRow';
import TableStateBody from '@/components/TableStateBody';
import PageHeader from '@/components/PageHeader';
import TableFilters from '@/components/TableFilters';
import useFetchList from '@/hooks/useFetchList';

const PARTNER_COLUMNS = 7;
const PARTNER_STATUSES = ['pending', 'approved', 'rejected', 'suspended'];

function matchesSearch(partner, query) {
  if (!query) return true;
  const s = query.toLowerCase();
  return (
    partner.business_name.toLowerCase().includes(s) ||
    partner.base_postcode.toLowerCase().includes(s) ||
    partner.category.toLowerCase().includes(s)
  );
}

export default function Partners() {
  const {
    data: partners,
    setData: setPartners,
    loading,
    refresh: fetchPartners,
  } = useFetchList('/admin/partners', { errorLabel: 'partners' });

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const handleAction = useCallback(
    async (partnerId, action) => {
      setActionLoading((s) => ({ ...s, [partnerId]: action }));
      try {
        const { data } = await api.patch(`/admin/partners/${partnerId}/${action}`);
        setPartners((prev) => prev.map((p) => (p.id === partnerId ? data : p)));
        toast.success(`Partner ${action}d`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to ${action} partner`);
      } finally {
        setActionLoading((s) => ({ ...s, [partnerId]: null }));
      }
    },
    [setPartners],
  );

  const filtered = useMemo(
    () =>
      partners.filter((p) => {
        if (statusFilter !== 'all' && p.status !== statusFilter) return false;
        return matchesSearch(p, search);
      }),
    [partners, statusFilter, search],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manage Partners"
        subtitle="Approve, reject or suspend partner accounts"
        onRefresh={fetchPartners}
        loading={loading}
        refreshTestId="btn-refresh-partners"
      />

      <Card className="p-4 border-slate-200">
        <TableFilters
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by business, postcode or category"
          searchTestId="partners-search"
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statuses={PARTNER_STATUSES}
          statusTestId="partners-status-filter"
          statusLabel={(s) => s.charAt(0).toUpperCase() + s.slice(1)}
        />

        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Postcode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableStateBody
                loading={loading}
                isEmpty={filtered.length === 0}
                colSpan={PARTNER_COLUMNS}
                emptyText="No partners found"
              >
                {filtered.map((p) => (
                  <PartnerRow
                    key={p.id}
                    partner={p}
                    actionLoading={actionLoading[p.id]}
                    onAction={handleAction}
                  />
                ))}
              </TableStateBody>
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
